/**
 * Ownership display helpers — pure, framework-free.
 *
 * The backend always returns quotations with a normalized ``owner`` object
 * (or ``null`` when unassigned). These helpers turn that shape into
 * user-facing Bulgarian strings and stable initials for avatar rendering.
 */

/**
 * Return the Bulgarian display label for an owner.
 * @param {{display_name?: string|null, email?: string|null, user_id?: string, is_active?: boolean}|null} owner
 * @param {(key:string)=>string} t  i18n translator
 * @returns {string}
 */
export function ownerLabel(owner, t) {
  if (!owner) return t("quotations.owner_unassigned");
  // Phase 2B.2 — never fall back to a UUID fragment. The backend supplies
  // ``display_label`` (already resolved to a human string); older payloads
  // fall through to the neutral Bulgarian label.
  const name =
    owner.display_label ||
    owner.display_name ||
    owner.email ||
    t("quotations.owner_user_fallback");
  const suffix = owner.is_active === false ? t("quotations.owner_inactive_suffix") : "";
  return `${name}${suffix}`;
}

/** Compact initials for avatars, e.g. "Иван Петров" → "ИП". */
export function ownerInitials(owner) {
  if (!owner) return "—";
  const src = owner.display_name || owner.display_label || owner.email || "";
  if (!src) return "?";
  const parts = src.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

/** Cross-user comparison used to check "is the current auth user" cases. */
export function isSameUser(owner, currentUserId) {
  if (!owner || !currentUserId) return false;
  return owner.user_id === currentUserId;
}
